<template>
  <v-dialog :value="sectionEditor.show" :width="1200" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <h3 class="label-input">Section Name</h3>
          <v-text-field v-model="fields.title" label="Name" />
        </div>

        <div class="mb-6">
          <h3 class="label-input">Header</h3>
          <ckeditor :editor="editor" v-model="fields.header" :config="editorConfig" />
        </div>

        <div class="mb-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <h3 class="label-input mb-0">Key Features ({{ fields.items.length }})</h3>
            <v-btn small color="primary" @click="handleAddItem">
              <v-icon size="16" left>mdi-plus</v-icon>
              Add Feature
            </v-btn>
          </div>

          <div v-if="fields.items.length === 0" class="text-center pa-4 grey--text group-item">
            No features added yet. Click "Add Feature" to get started.
          </div>

          <v-expansion-panels v-else accordion>
            <v-expansion-panel v-for="(item, i) in fields.items" :key="i">
              <v-expansion-panel-header>
                <div class="d-flex justify-space-between align-center" style="width: 100%">
                  <div class="d-flex align-center">
                    <div class="feature-thumbnail mr-3">
                      <img v-if="item.image" :src="item.image" alt="" />
                      <v-icon v-else color="grey lighten-1">mdi-image</v-icon>
                    </div>
                    <div>Key Feature {{ i + 1 }}</div>
                  </div>
                  <v-btn icon small @click.stop="handleRemoveItem(i)">
                    <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="pa-2">
                  <div class="mb-4">
                    <UploadImage
                      :ref="`featureImageUpload${i}`"
                      @callback="obj => handleUploadImage(obj, i)"
                      :image="{ imageSrc: item.image }"
                      :height="`120`"
                      :typeUpload="'image'"
                      @callbackDelete="() => handleRemoveImage(i)"
                    />
                  </div>
                  <ckeditor :editor="editor" v-model="item.content" :config="editorConfig" />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <div>
          <label class="label-input">Section Background</label>
          <div class="group-item">
            <v-row>
              <v-col cols="6">
                <v-checkbox v-model="fields.topPadding" label="Top Padding" />
              </v-col>
              <v-col cols="6">
                <v-checkbox v-model="fields.bottomPadding" label="Bottom Padding" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="12">
                <div class="d-flex align-center gap-3">
                  <input
                    type="color"
                    name="backgroundColor"
                    style="width: 32px; height: 32px; margin-right: 12px"
                    :value="fields.colors.backgroundColor"
                    @change="handleChangeBackgroundColor"
                  />
                  <v-text-field v-model="fields.colors.backgroundColor" label="Background Color" small dense />
                  <v-btn @click="handleResetBackgroundColor" small text color="primary"> Reset </v-btn>
                </div>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save Changes</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { cloneDeepObject } from '../common/utils';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { replaceImageUrl } from '../common/utils';

export default {
  name: 'cardKeyFeaturesSectionEditor',
  components: {
    UploadImage,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Key Features',
        header: '',
        items: [],
        topPadding: true,
        bottomPadding: true,
        colors: {
          backgroundColor: '#ffffff',
        },
      },
    };
  },
  methods: {
    handleClosePopup() {
      this.$emit('close');
    },

    handleSave() {
      const updatedSection = {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtmlFromData.sectionCardKeyFeatures(this.sectionEditor.section.html, this.fields),
      };
      this.$emit('update:section', updatedSection);
      this.handleClosePopup();
    },

    handleAddItem() {
      this.fields.items.push({
        image: replaceImageUrl(
          'https://cdn.skyfastimg.com/images/2026/02/27/image-69a16146210da7f3d14ab768.webp?w=50&h=50&q=80',
        ),
        content: `<h3 style="text-align: center"><strong>Title</strong></h3><p style="text-align: center">Description</p>`,
      });
    },

    handleRemoveItem(index) {
      this.fields.items.splice(index, 1);
    },

    handleUploadImage(obj, index) {
      if (obj && obj.imageSrc) {
        this.fields.items[index].image = obj.imageSrc;
      }
    },

    handleRemoveImage(index) {
      this.fields.items[index].image = '';
    },
    handleChangeBackgroundColor(event) {
      this.fields.colors.backgroundColor = event.target.value;
    },
    handleResetBackgroundColor() {
      this.fields.colors.backgroundColor = '#ffffff';
    },
  },

  mounted() {
    if (this.sectionEditor.sectionExtract) {
      this.fields = cloneDeepObject(this.sectionEditor.sectionExtract);
    }
  },

  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal.sectionExtract) {
          this.fields = cloneDeepObject(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .ck-editor__editable_inline {
  min-height: 100px !important;
}
::v-deep .ck-editor__editable {
  min-height: 100px !important;
}

.label-input {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  display: block;
}

.header-modal,
.footer-modal {
  padding: 0 24px;
  border-bottom: 1px solid #e0e0e0;
}

.footer-modal {
  border-top: 1px solid #e0e0e0;
  border-bottom: none;
}

.d-flex {
  display: flex;
}

.align-center {
  align-items: center;
}

.gap-3 {
  gap: 12px;
}

.feature-thumbnail {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

::v-deep .v-text-field--outlined {
  .v-input__control {
    .v-input__slot {
      min-height: 40px;
    }
  }
}
</style>
